import { accommodationSubTypeIdZodSchema } from "@@/shared/acquire/resources/resource-accommodation/accommodation-types/accommodation-sub-type-id";
import { capacityZodObject } from "@@/shared/acquire/resources/resource-accommodation/capacity";
import { isResourceDefinitionOf } from "@@/shared/acquire/resources/resource-definition-helpers";
import { isResourceFoundationOf } from "@@/shared/acquire/resources/resource-foundation-helpers";
import { resourceFoundationIdZodSchema } from "@@/shared/acquire/resources/resource-foundation-id";
import {
    isMatchingResourceParts,
    type MatchingResourceParts,
} from "@@/shared/acquire/resources/resource-parts";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { metaContentZodSchema } from "@@/shared/meta-content/meta-content";
import { providerIdZodSchema } from "@@/shared/providers/provider-id";
import { unitZodSchema } from "@@/shared/units-and-quantities/unit";
import { userIdZodSchema } from "@@/shared/users/user-id";
import { shallowCleanObjectFromNulls } from "@@/utilities/clean";
import { emptyArrayOf } from "@@/utilities/empty";
import { z } from "zod";
import { accommodationTypeZodObject } from "../../resources/resource-accommodation/accommodation-types/accommodation-type";
import {
    resourceImageReferenceZodObject,
    type ResourceImageReference,
} from "../../resources/resource-accommodation/accommodation-types/resource-image-reference";
import {
    amenityZodObject,
    type Amenity,
} from "../../resources/resource-accommodation/amenities/amenity";
import { resourceIdFactory, resourceIdZodSchema } from "../resource-id";
import { resourceShortNameZodString } from "../resource.short-name";

/** A resource that is of accommodation type */
const resourceAccommodationZodObject = z.object({
    _id: resourceIdZodSchema,
    _type: z.literal("RESOURCE_ACCOMMODATION"),
    _createdAt: isoAndUnixZodSchema,
    _createdBy: userIdZodSchema,
    // slug: z.string().regex(/^[a-z0-9-]+$/),
    accommodationType: accommodationTypeZodObject,
    meta: metaContentZodSchema,
    amenities: z.array(amenityZodObject), // TODO! Vill vi ha det såhär? object eller idn?
    ownerId: providerIdZodSchema,
    quantity: z.number().int().min(1).max(1),
    unit: unitZodSchema, // todo! Vad använder vi unit till @jakob? mycket breaker lite här och där om man tar bort den.
    imageRefs: z.array(resourceImageReferenceZodObject).optional(),
    capacity: capacityZodObject,
    disabledAccommodationSubTypeIds: z
        .array(accommodationSubTypeIdZodSchema)
        .optional(),
    /** If `foundationId is set, the resource was generated
     * using a foundation and a definition. Foundation could be edited and
     * all resources with that foundation would then be changed, if value
     * edited had not already been overiden in their definitions that is */
    foundationId: resourceFoundationIdZodSchema.optional(),
    /** If `true` `foundationId` has to be available and the definition is
     * departing from the foundation in some ways */
    hasDefinitionOverrides: z.coerce.boolean().optional(),
    /** If `true` the resource can be checked in.
     * If `false` the resource can not be checked in.
     * If not set, it defaults to `false`. */
    checkInEnabled: z.coerce.boolean().optional(),
    /** If `true` the resource allows unlimited number
     * of check ins to be true at once.
     * If `false` the resource can only be checked in
     * by one reservation at a time.
     * If not set, it defaults to `false`. */
    allowMultipleCheckIns: z.coerce.boolean().optional(),
    /**
     * Optional short name, e.g. B12
     * if not set a short name will be generated based on
     * the title when needed. Max 5 chars.
     */
    shortName: resourceShortNameZodString.optional(),
});
/** A resource that is of accommodation type */
type ResourceAccommodation = z.infer<typeof resourceAccommodationZodObject>;

const isResourceAccommodation = (
    item: unknown,
): item is ResourceAccommodation => {
    return (item as ResourceAccommodation)?._type === "RESOURCE_ACCOMMODATION";
};

const resourceAccommodationFactoryParamsZodObject =
    resourceAccommodationZodObject
        .omit({
            _id: true,
            _type: true,
            _createdAt: true,
        })
        .and(z.object({ _id: resourceIdZodSchema.optional() }));
type ResourceAccommodationFactoryParams = z.infer<
    typeof resourceAccommodationFactoryParamsZodObject
>;

const isResourceAccommodationParts =
    isMatchingResourceParts<ResourceAccommodation>("RESOURCE_ACCOMMODATION");
const isResourceAccommodationFoundation =
    isResourceFoundationOf<ResourceAccommodation>("RESOURCE_ACCOMMODATION");
const isResourceAccommodationDefinition =
    isResourceDefinitionOf<ResourceAccommodation>("RESOURCE_ACCOMMODATION");

function resourceAccommodationFactory(
    params: ResourceAccommodationFactoryParams,
): ResourceAccommodation;
function resourceAccommodationFactory(
    params: MatchingResourceParts<ResourceAccommodation>,
): ResourceAccommodation;
function resourceAccommodationFactory(
    params:
        | ResourceAccommodationFactoryParams
        | MatchingResourceParts<ResourceAccommodation>,
): ResourceAccommodation;
function resourceAccommodationFactory(
    params:
        | ResourceAccommodationFactoryParams
        | MatchingResourceParts<ResourceAccommodation>,
): ResourceAccommodation {
    if (isResourceAccommodationParts(params)) {
        // MatchingResourceParts<ResourceAccommodation>
        const foundation = params.foundation;
        const definition = params.definition;
        if (foundation._id !== definition.foundationId) {
            throw new Error("Definition must be based on the given foundation");
        }
        const resource: ResourceAccommodation = {
            ...foundation.data,
            meta: {
                ...foundation.data.meta,
                ...(definition.data?.title
                    ? {
                          title: definition.data.title,
                      }
                    : {}),
                ...(definition.data?.subtitle
                    ? {
                          subtitle: definition.data.subtitle,
                      }
                    : {}),
                ...(definition.data?.description
                    ? {
                          description: definition.data.description,
                      }
                    : {}),
            },
            amenities:
                definition.data.amenities ||
                foundation.data.amenities ||
                emptyArrayOf<Amenity>(),
            imageRefs:
                definition.data.imageRefs ||
                foundation.data.imageRefs ||
                emptyArrayOf<ResourceImageReference>(),
            capacity: {
                ...foundation.data.capacity,
                ...shallowCleanObjectFromNulls(definition.data.capacity),
            },
            checkInEnabled:
                definition.data.checkInEnabled ||
                foundation.data.checkInEnabled ||
                false,
            allowMultipleCheckIns:
                definition.data.allowMultipleCheckIns ||
                foundation.data.allowMultipleCheckIns ||
                false,
            hasDefinitionOverrides: definition.hasOverrides,
            foundationId: definition.foundationId,
            _id: definition._id,
        };
        return resource;
    }

    // ResourceAccommodationFactoryParams
    return {
        ...params,
        _id: params._id ?? resourceIdFactory(),
        _createdAt: nowIsoAndUnix(),
        _type: "RESOURCE_ACCOMMODATION",
    };
}

export {
    isResourceAccommodation,
    isResourceAccommodationDefinition,
    isResourceAccommodationFoundation,
    isResourceAccommodationParts,
    resourceAccommodationFactory,
    resourceAccommodationFactoryParamsZodObject,
    resourceAccommodationZodObject,
};
export type { ResourceAccommodation, ResourceAccommodationFactoryParams };
